<popup-c class="no-content-padding bs-enabled" size="large" width="1100px"
         head="Create a Quiz"
         subTitle="Build a custom security awareness quiz for your training campaigns"
         (closeCb)="cancelCb.emit()">
    <div class="flex-column f-1-0 pt-2 gap-2" style="padding: 20px; overflow: auto;">

        <!--steps progress-->
        <div class="steps-container" style="font-size: 0.8rem;">
            <div class="step-tab"
                 *ngFor="let step of quizCreationSteps | keyvalue: gs.returnZero"
                 [ngClass]="{'active': currentStep === step.value}">
                {{step.value}}
            </div>
        </div>

        <!--STEP: Instructions-->
        <div class="flex-column overflow-auto animated fadeIn f-1-0 gap-3 p-2 pt-3"
             style="line-height: 2;"
             *ngIf="currentStep === quizCreationSteps.instructions">
            <span>
                This wizard will guide you through creating a custom quiz for your security awareness training.
                Quizzes are assigned to recipients as part of a training campaign. Recipients complete the quiz
                at their own pace and receive a score upon completion.
            </span>
            <br/>
            <div class="flex-row gap-2 align-items-baseline">
                <span class="fw-bold">What's next?</span>
            </div>
            <div class="bg-light p-2">
                <ul style="list-style-type: number;">
                    <li><span><b>Quiz Info</b> — Enter the quiz name, description, and optional custom instructions.</span></li>
                    <li><span><b>Multiple-Choice Questions</b> — Add standard quiz questions with 2–4 answer options. Mark the correct answer for each question.</span></li>
                    <li><span><b>Summary & Execute</b> — Review a summary and preview the quiz before creating it.</span></li>
                </ul>
            </div>
        </div>

        <!--STEP: Quiz Info-->
        <div class="flex-column f-1-0 animated fadeIn gap-3 p-2 pt-3"
             *ngIf="currentStep === quizCreationSteps.quizInfo">
            <div class="list-group list-group-flush settings-list-container">
                <div class="list-group-item settings-list-item py-4">
                    <div class="title-section-container flex-column gap-2">
                        <b>Quiz Name</b>
                        <span style="font-size: 0.9em;">The name will be displayed in the training materials list.</span>
                    </div>
                    <div class="content-container flex-column gap-3">
                        <input class="m-0 w-100" type="text"
                               [(ngModel)]="data.name"
                               [ngClass]="{'validation-error': error && !data.name}"
                               (keyup)="error = false"
                               placeholder="Enter quiz name">
                    </div>
                </div>
                <div class="list-group-item settings-list-item py-4">
                    <div class="title-section-container flex-column gap-2">
                        <b>Description <span class="fw-normal text-muted">(optional)</span></b>
                        <span style="font-size: 0.9em;">A short description that will be shown at the quiz start screen.</span>
                    </div>
                    <div class="content-container flex-column gap-3">
                        <textarea class="m-0 color-equalized w-100" style="height: 70px;"
                                  placeholder="Enter quiz description"
                                  [(ngModel)]="data.description"></textarea>
                    </div>
                </div>
                <div class="list-group-item settings-list-item py-4">
                    <div class="title-section-container flex-column gap-2">
                        <b>Instructions <span class="fw-normal text-muted">(optional)</span></b>
                        <span style="font-size: 0.9em;">Custom instructions that will be shown at the quiz start screen. If left empty, default instructions will be displayed.</span>
                    </div>
                    <div class="content-container flex-column gap-3">
                        <textarea class="m-0 color-equalized w-100" style="height: 100px;"
                                  placeholder="Enter custom instructions for quiz takers"
                                  [(ngModel)]="data.quiz_config.instructions"></textarea>
                    </div>
                </div>
                <div class="list-group-item settings-list-item py-4">
                    <div class="title-section-container flex-column gap-2">
                        <b>Passing Score</b>
                        <span style="font-size: 0.9em;">Set the minimum score recipients must achieve to pass this quiz.</span>
                    </div>
                    <div class="content-container align-items-center flex-row gap-3">
						<checkbox id="quizPassingScoreEnabled"
								  [(model)]="data.quiz_config.enable_passing_score">
							"Pass" is a score equal or above
						</checkbox>
						<div class="flex-row align-items-center gap-1">
							<input type="number" class="m-0" style="width: 55px;"
								   min="1" max="100"
								   [disabled]="!data.quiz_config.enable_passing_score"
								   [(ngModel)]="data.quiz_config.passing_score" />
							<span>%</span>
						</div>
					</div>
                </div>
                <div class="list-group-item settings-list-item py-4">
                    <div class="title-section-container flex-column gap-2">
                        <b>Questions per session</b>
                        <span style="font-size: 0.9em;">Choose how many questions recipients will see. When limited, questions are randomly selected from the full pool each session.</span>
                    </div>
                    <div class="content-container align-items-center flex-row gap-3">
						<checkbox id="inboundRulesResultNotifyRecipient"
								  [(model)]="data.quiz_config.enable_questions_count">
							Limit to a specific number
						</checkbox>
						<input type="number" class="m-0" style="width: 55px;"
							   min="1"
							   [disabled]="!data.quiz_config.enable_questions_count"
							   [(ngModel)]="data.quiz_config.questions_count" />
					</div>
                </div>
            </div>
        </div>

        <!--STEP: Multiple-Choice Questions-->
        <div class="flex-column f-1-0 animated fadeIn gap-0"
             *ngIf="currentStep === quizCreationSteps.multipleChoiceQuestions">
            <div class="bg-white border-bottom p-3 flex-shrink-0">
                <span>Add multiple-choice questions below. Enter the question text, fill in the answer options, and <b>click an option row</b> to mark it as correct.</span>
            </div>
            <div class="flex-column gap-3 p-3 overflow-auto f-1-0">
                <div *ngFor="let mcQuestion of data.multipleChoiceQuestions; let i = index; trackBy: trackByIndex"
                     class="quiz-question-card flex-column gap-3">
                    <div class="flex-row align-items-center gap-2">
                        <span class="question-number-badge">{{i + 1}}</span>
                        <input type="text" class="f-1-0 m-0"
                               [(ngModel)]="mcQuestion.question"
                               [ngClass]="{'validation-error': error && !mcQuestion.question}"
                               (keyup)="error = false"
                               placeholder="Type your question here...">
                        <div class="toggled-menu-container" style="position: relative;"
                             (click)="$event.stopPropagation();">
                            <i class="fas fa-ellipsis-h bg-light border border-2 rounded-2" style="padding: 5px; cursor: pointer;"
                               [id]="'qMenu' + mcQuestion.id + 'Trigger'"
                               tooltip="Options"
                               (click)="mcQuestion.showMenu = !mcQuestion.showMenu"></i>
                            <div class="drop-menu" style="right: 0; left: auto; min-height: 0;" [ngClass]="{'active': mcQuestion.showMenu}">
                                <ul (clickOutside)="mcQuestion.showMenu = false;"
                                    [exclude]="'#qMenu' + mcQuestion.id + 'Trigger'">
                                    <li class="flex-row align-items-center"
                                        (click)="duplicateMCQuestion(i); mcQuestion.showMenu = false;">
                                        <span>Duplicate</span>
                                    </li>
                                    <li class="flex-row align-items-center text-danger"
                                        *ngIf="data.multipleChoiceQuestions.length > 1"
                                        (click)="removeMCQuestion(i); mcQuestion.showMenu = false;">
                                        <span>Remove</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="flex-column gap-2">
                        <div *ngFor="let opt of mcQuestion.options; let j = index; trackBy: trackByIndex"
                             class="quiz-option-row flex-row gap-2 align-items-center"
                             [ngClass]="{'is-correct': mcQuestion.correctIndex === j}"
                             (click)="mcQuestion.correctIndex = j">
                            <input type="radio"
                                   style="display: none;"
                                   [name]="'correct-' + mcQuestion.id"
                                   [value]="j"
                                   [(ngModel)]="mcQuestion.correctIndex"/>
                            <span class="option-letter-badge"
                                  tooltip="Mark as correct answer">
                                <i *ngIf="mcQuestion.correctIndex === j" class="far fa-check" style="line-height: normal;"></i>
                                <ng-container *ngIf="mcQuestion.correctIndex !== j">{{optionLetters[j]}}</ng-container>
                            </span>
                            <input type="text" class="f-1-0 m-0 quiz-option-input"
                                   [(ngModel)]="mcQuestion.options[j]"
                                   [ngClass]="{'validation-error': error && !mcQuestion.options[j]}"
                                   (keyup)="error = false"
                                   (click)="$event.stopPropagation()"
                                   placeholder="Answer option {{j + 1}}"/>
                            <i class="zmdi zmdi-close quiz-remove-option-btn"
							   tooltip="Remove this option slot"
                               *ngIf="mcQuestion.options.length > 2"
                               (click)="$event.stopPropagation(); removeOption(mcQuestion, j)"></i>
                        </div>
                    </div>
                    <div class="flex-column gap-2">
						<button type="button" class="quiz-add-btn"
								[tooltip]="mcQuestion.options.length >= 4 ? 'Maximum of 4 answer options per question' : null"
								[disabled]="mcQuestion.options.length >= 4"
								(click)="addOption(mcQuestion)">
							<i class="far fa-plus"></i>
							<span>Add Answer</span>
						</button>
						<div class="flex-row align-items-start gap-2">
                            <i class="far fa-info-circle text-muted"
                               style="flex-shrink: 0; margin-top: 7px;"
                               tooltip="Shown to recipients after they submit the quiz. Use it to explain why the correct answer is right."></i>
                            <textarea class="f-1-0 m-0 color-equalized" style="height: 54px; resize: none;"
                                      [(ngModel)]="mcQuestion.explanation"
                                      placeholder="Explanation (optional)"></textarea>
                        </div>
                    </div>
                </div>
                <div class="flex-row gap-2">
                    <button type="button" class="quiz-add-question-btn" (click)="addMCQuestion()">
                        <i class="far fa-plus"></i>
                        <span>Add Question</span>
                    </button>
                    <button type="button" class="quiz-add-question-btn" (click)="openCsvImportPopup()">
                        <i class="far fa-upload"></i>
                        <span>Import from CSV</span>
                    </button>
                </div>
            </div>
        </div>

        <!--STEP: Summary & Execute-->
        <div class="flex-column f-1-0 animated fadeIn gap-3 p-2 pt-3 overflow-auto"
             *ngIf="currentStep === quizCreationSteps.summary">
            <div class="flex-column gap-4 py-2 px-4">
                <div class="flex-row gap-2 align-items-center">
                    <span class="fw-500" style="flex-basis: 200px; flex-shrink: 0;">Quiz name:</span>
                    <span>{{data.name}}</span>
                </div>
                <div class="flex-row gap-2 align-items-start" *ngIf="data.description">
                    <span class="fw-500" style="flex-basis: 200px; flex-shrink: 0;">Description:</span>
                    <span>{{data.description}}</span>
                </div>
                <div class="flex-row gap-2 align-items-center">
                    <span class="fw-500" style="flex-basis: 200px; flex-shrink: 0;">Multiple-choice questions:</span>
                    <span>{{data.multipleChoiceQuestions.length}}</span>
                </div>
                <div class="flex-row gap-2 align-items-center">
                    <span class="fw-500" style="flex-basis: 200px; flex-shrink: 0;">Questions per session:</span>
                    <span>{{data.quiz_config.questions_count !== null ? data.quiz_config.questions_count : 'All'}}</span>
                </div>
            </div>
            <hr class="my-0"/>
            <div class="f-1-0 overflow-auto" style="min-height: 400px;">
                <threat-simulation-quiz-preview
                    *ngIf="previewMaterialObj"
                    [materialObj]="previewMaterialObj">
                </threat-simulation-quiz-preview>
            </div>
        </div>

        <!--buttons-->
        <div class="flex-row justify-content-between align-items-end mb-3 px-4 w-100">
            <div class="f-1-0"></div>
            <div class="flex-row gap-3" style="font-size: 13px;">
                <btn-c class="popup-action-btn"
                       *ngIf="currentStep === quizCreationSteps.instructions"
                       variant="outlined"
                       (action)="cancelCb.emit()">
                    Cancel
                </btn-c>
                <btn-c class="popup-action-btn"
                       *ngIf="currentStep !== quizCreationSteps.instructions"
                       variant="outlined"
                       [isDisabled]="createInProcess"
                       (action)="backStep()">
                    Back
                </btn-c>
                <btn-c class="popup-action-btn"
                       *ngIf="currentStep !== quizCreationSteps.summary"
                       (action)="nextStep()">
                    Next
                </btn-c>
                <btn-c class="popup-action-btn"
                       *ngIf="currentStep === quizCreationSteps.summary"
                       [isDisabled]="createInProcess"
                       [loading]="createInProcess"
                       (action)="createQuiz()">
                    {{isEditMode ? 'Save Changes' : 'Create Quiz'}}
                </btn-c>
            </div>
        </div>

    </div>
</popup-c>

<popup-c class="bs-enabled" *ngIf="importQuizQuestionsPopup?.show"
         head="Import Questions from CSV"
         size="medium" height="670px"
		 (closeCb)="importQuizQuestionsPopup = null"
         (confirmCb)="importCsvQuestions()"
         [buttons]="{cancel: 'Cancel', confirm: {text: 'Import', disabled: !importQuizQuestionsPopup.file}}">
    <div class="flex-column gap-4">

        <div class="flex-column gap-3">
            <div class="flex-column gap-2">
                <span class="fw-500">Instructions</span>
                <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                    <li>The file must be in <b>.csv</b> format</li>
                    <li>Each row represents one question — no header row needed</li>
                    <li>The <b>first option is always the correct answer</b></li>
                    <li>Options will be shuffled automatically on import</li>
                    <li>An optional explanation can be added in the <b>6th column</b> — if you have fewer than 4 options leave the unused option columns empty so the explanation stays in position 6</li>
                </ul>
            </div>
            <div class="flex-column gap-2">
                <span class="fw-500">Example</span>
                <table style="border-collapse: collapse; font-size: 0.92em; width: 100%;">
                <tr class="bg-light">
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Question</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px; color: var(--bs-success); font-weight: 600; text-transform: uppercase;">Correct Answer</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Option 2</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Option 3 (optional)</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Option 4 (optional)</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Explanation (optional)</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">What is phishing?</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px; color: var(--bs-success);">Tricks users into revealing credentials</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">A type of firewall</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px; color: #aaa; font-style: italic;">(empty)</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px; color: #aaa; font-style: italic;">(empty)</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Phishing uses fake emails to steal sensitive data</td>
                </tr>
                <tr>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">What should you do with a suspicious link?</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px; color: var(--bs-success);">Verify the sender before clicking</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Click to check if it's safe</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Forward to colleagues</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;">Report to IT</td>
                    <td style="border: 1px solid #dee2e6; padding: 6px 10px;"></td>
                </tr>
            </table>
            </div>
        </div>

        <!--CC: highlighted picker box (was a bare label) + height auto so the native "Choose File" button is not clipped (QA)-->
        <div class="flex-column gap-2 border bg-light p-3 rounded">
            <span class="fw-500">Select CSV file:</span>
            <input type="file" accept=".csv" style="height: auto; padding: 4px 0; border: none; background: transparent;" (change)="onCsvFileSelected($event)">
        </div>

        <div class="flex-column gap-2 my-2 border bg-light p-2" *ngIf="importQuizQuestionsPopup.hasExistingQuestions">
            <span class="fw-500">You already have questions in this quiz:</span>
            <div class="flex-column gap-1">
                <label class="flex-row gap-2 align-items-center" style="cursor: pointer;">
                    <input type="radio" name="csvImportMode" [(ngModel)]="importQuizQuestionsPopup.importMode" value="append">
                    <span>Append to existing questions</span>
                </label>
                <label class="flex-row gap-2 align-items-center" style="cursor: pointer;">
                    <input type="radio" name="csvImportMode" [(ngModel)]="importQuizQuestionsPopup.importMode" value="replace">
                    <span>Replace existing questions</span>
                </label>
            </div>
        </div>

    </div>
</popup-c>
